"use client";

import { INTERNAL_LINK } from "@/types";
import Link from "next/link";
import { useEffect, useState } from "react";
import "@/styles/Cookies.scss";

const updateConsentMode = (prefs: { analytics: boolean; marketing: boolean }) => {
	if (typeof window !== "undefined" && typeof window.gtag === "function") {
		window.gtag("consent", "update", {
			ad_storage: prefs.marketing ? "granted" : "denied",
			analytics_storage: prefs.analytics ? "granted" : "denied",
			ad_user_data: prefs.marketing ? "granted" : "denied",
			ad_personalization: prefs.marketing ? "granted" : "denied",
		});
	}
};

const CookieConsent = () => {
	const [isVisible, setIsVisible] = useState(false);
	const [cookieOptionsOpen, setCookieOptionsOpen] = useState(false);
	const [preferences, setPreferences] = useState({
		necessary: true, // Immer aktiv
		analytics: false,
		marketing: false,
	});

	useEffect(() => {
		const storedConsent = localStorage.getItem("cookieConsent");
		if (!storedConsent) {
			setIsVisible(true);
		}
	}, []);

	// Bei Reset
	useEffect(() => {
		const handler = (event: Event) => {
			const detail = (event as CustomEvent).detail;
			if (detail === null) {
				setIsVisible(true);
				setPreferences({
					necessary: true,
					analytics: false,
					marketing: false,
				});
			}
			location.reload();
		};
		window.addEventListener("cookiePreferencesReset", handler);
		return () => window.removeEventListener("cookiePreferencesReset", handler);
	}, []);

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = e.target;
		setPreferences((prev) => ({ ...prev, [name]: checked }));
	};

	const savePreferences = () => {
		localStorage.setItem("cookieConsent", JSON.stringify(preferences));
		updateConsentMode(preferences);
		setIsVisible(false);
		window.dispatchEvent(new CustomEvent("cookiePreferencesChanged", { detail: preferences }));
	};

	const rejectAll = () => {
		const basePrefs = { necessary: true, analytics: false, marketing: false };
		localStorage.setItem("cookieConsent", JSON.stringify(basePrefs));
		updateConsentMode(basePrefs);
		setPreferences(basePrefs);
		setIsVisible(false);
		window.dispatchEvent(new CustomEvent("cookiePreferencesChanged", { detail: basePrefs }));
	};

	const acceptAll = () => {
		const prefs = { necessary: true, analytics: true, marketing: true };
		localStorage.setItem("cookieConsent", JSON.stringify(prefs));
		updateConsentMode(prefs);
		setPreferences(prefs);
		setIsVisible(false);
		window.dispatchEvent(new CustomEvent("cookiePreferencesChanged", { detail: prefs }));
	};

	if (!isVisible) return null;

	return (
		<div className="cookie-banner">
			<p>
				Wir verwenden Cookies und ähnliche Technologien, um Dienste auf unserer Website bereitzustellen und dein Nutzungserlebnis zu verbessern. Du kannst selbst entscheiden, welche Kategorien du
				zulassen möchtest.{" "}
				<Link href={INTERNAL_LINK.Datenschutz} className="cookie-link">
					Mehr erfahren
				</Link>
			</p>

			<div className="cookie-options">
				<label className="cookie-label">
					<input type="checkbox" checked disabled />
					Essentiell
				</label>
				<label className="cookie-label">
					<input type="checkbox" name="analytics" checked={preferences.analytics} onChange={handleCheckboxChange} />
					Statistiken
				</label>
				<label className="cookie-label">
					<input type="checkbox" name="marketing" checked={preferences.marketing} onChange={handleCheckboxChange} />
					Marketing
				</label>
			</div>
			<div className="cookie-buttons flex">
				<button className="cookie-button cookie-button-gray" onClick={rejectAll}>
					Nur notwendige Cookies
				</button>
				{preferences.necessary && !preferences.analytics && !preferences.marketing ? (
					<button className="cookie-button" onClick={acceptAll}>
						Alle Cookies akzeptieren
					</button>
				) : (
					<button className="cookie-button" onClick={savePreferences}>
						Auswahl speichern
					</button>
				)}
			</div>
		</div>
	);
};

export default CookieConsent;
